import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUpRight, Sparkles, Crown, Camera } from "lucide-react";

const COUNTRIES = [
  { code: "US", flag: "🇺🇸", name: "United States" },
  { code: "NG", flag: "🇳🇬", name: "Nigeria" },
  { code: "UK", flag: "🇬🇧", name: "United Kingdom" },
  { code: "CA", flag: "🇨🇦", name: "Canada" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [country, setCountry] = useState("US");
  const [trending, setTrending] = useState([]);
  const [loadingTrend, setLoadingTrend] = useState(false);

  useEffect(() => {
    api.get("/analyses").then((r) => setAnalyses(r.data.analyses || []));
  }, []);

  useEffect(() => {
    setLoadingTrend(true);
    api.get(`/hairstyles/trending/${country}`)
      .then((r) => setTrending(r.data.hairstyles || []))
      .finally(() => setLoadingTrend(false));
  }, [country]);

  const free_remaining = user?.is_premium ? "∞" : Math.max(0, 3 - (user?.free_analyses_used || 0));
  const latest = analyses[0];

  return (
    <main className="crown-container py-12 md:py-16" data-testid="dashboard-page">
      {/* Hero card */}
      <section className="grid md:grid-cols-12 gap-8 mb-16">
        <div className="md:col-span-7 animate-fade-up">
          <p className="font-overline mb-3">Welcome back</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] mb-4">
            Good to see you, <span className="italic text-gold">{user?.name?.split(" ")[0] || "friend"}.</span>
          </h1>
          <p className="text-ash leading-relaxed mb-8 max-w-lg">
            Upload a selfie to receive a fresh editorial reading — face shape, hair texture and
            a curated set of styles you'll actually wear.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/analyze" className="crown-btn-primary" data-testid="dashboard-analyze-cta">
              <Camera size={16} strokeWidth={1.5} /> Start an analysis
            </Link>
            <Link to="/occasion" className="crown-btn-ghost" data-testid="dashboard-occasion-cta">
              Ceremony reading
            </Link>
            <Link to="/gallery" className="crown-btn-ghost" data-testid="dashboard-gallery-cta">
              Browse gallery
            </Link>
          </div>
        </div>
        <div className="md:col-span-5 grid grid-cols-2 gap-3">
          <div className="bg-obsidian p-6 border border-bone/10" data-testid="dashboard-stat-analyses">
            <p className="font-overline text-ash">Analyses</p>
            <p className="font-serif text-5xl mt-3 text-gold">{analyses.length}</p>
            <p className="text-xs text-ash mt-2">All time</p>
          </div>
          <div className="bg-obsidian p-6 border border-bone/10" data-testid="dashboard-stat-quota">
            <p className="font-overline text-ash">Quota</p>
            <p className="font-serif text-5xl mt-3 text-gold">{free_remaining}</p>
            <p className="text-xs text-ash mt-2">{user?.is_premium ? "Premium · unlimited" : "Free analyses left"}</p>
          </div>
          <div className={`p-6 border col-span-2 ${user?.is_premium ? "bg-gold/10 border-gold/30" : "bg-obsidian border-bone/10"}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-overline text-gold flex items-center gap-2"><Crown size={14} strokeWidth={1.5} /> Membership</p>
                <p className="font-serif text-2xl mt-2">{user?.is_premium ? "Premium Atelier" : "Free tier"}</p>
              </div>
              {!user?.is_premium && (
                <Link to="/pricing" className="font-overline text-gold hover:text-gold-hover flex items-center gap-1" data-testid="dashboard-upgrade-link">
                  Upgrade <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Latest analysis */}
      {latest && (
        <section className="mb-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="font-overline">Most recent reading</p>
              <h2 className="font-serif text-3xl mt-2">Your latest analysis</h2>
            </div>
            <Link to={`/analysis/${latest.id}`} className="font-overline text-gold flex items-center gap-1" data-testid="latest-analysis-link">
              Open <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-bone/10">
            {[
              ["Face shape", latest.result.face_shape],
              ["Hair type", latest.result.hair_type],
              ["Density", latest.result.hair_density],
              ["Skin tone", latest.result.skin_tone],
            ].map(([k, v]) => (
              <div key={k} className="bg-espresso p-6">
                <p className="font-overline text-ash">{k}</p>
                <p className="font-serif text-2xl mt-2 capitalize text-gold">{v}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Trending */}
      <section data-testid="trending-section">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-overline flex items-center gap-2"><Sparkles size={12} strokeWidth={1.5} /> Trending</p>
            <h2 className="font-serif text-3xl mt-2">Now in <span className="italic text-gold">{COUNTRIES.find(c => c.code === country)?.name}</span></h2>
          </div>
          <div className="flex gap-2">
            {COUNTRIES.map((c) => (
              <button
                key={c.code}
                onClick={() => setCountry(c.code)}
                data-testid={`trending-country-${c.code}`}
                className={`px-4 py-2 font-overline text-xs border transition-colors ${
                  country === c.code
                    ? "border-gold text-gold bg-gold/5"
                    : "border-bone/15 text-ash hover:text-bone"
                }`}
              >
                {c.flag} {c.code}
              </button>
            ))}
          </div>
        </div>

        {loadingTrend ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[...Array(5)].map((_, i) => <div key={i} className="aspect-[3/4] bg-obsidian animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {trending.map((s) => (
              <Link key={s.id} to={`/style/${s.id}`} className="group relative aspect-[3/4] overflow-hidden" data-testid={`trending-card-${s.id}`}>
                <img src={s.image_url} alt={s.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="font-overline text-gold text-[0.65rem]">{s.category}</p>
                  <p className="font-serif text-lg mt-1 leading-tight">{s.name}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
